import { ReactNode } from 'react';

import {
  Stack,
  Box,
  Typography,
  TypographyProps,
  StackProps,
  BoxProps,
} from '@mui/material';
import { ArrayValues } from 'type-fest';
import clsx from 'clsx';

import { InfoTooltip } from '../../../components/tooltip/InfoTooltip';
import { tokens } from '../../../theme/generated/tokens';
import { mergeSx } from '../../../utils';
import { classNamesFor } from '../../../utils/classNames';

const classNamesFactory = classNamesFor('MetricsBox');

const metricsBoxClassNames = {
  root: classNamesFactory.root(),
  startContent: classNamesFactory.element('startContent'),
  label: classNamesFactory.element('label'),
  infoTooltip: classNamesFactory.element('infoTooltip'),
  value: classNamesFactory.element('value'),
  unit: classNamesFactory.element('unit'),
  footer: classNamesFactory.element('footer'),
};

export const metricsBoxSizes = ['xs', 's', 'm', 'l'] as const;

type MetricsBoxSize = ArrayValues<typeof metricsBoxSizes>;

export type MetricsBoxProps = {
  size?: MetricsBoxSize;
  testId?: string;
  slots?: {
    startContent?: ReactNode;
    label?: ReactNode;
    infoTooltip?: ReactNode;
    value?: ReactNode;
    unit?: ReactNode;
    footer?: ReactNode;
  };
  slotProps?: {
    root?: Partial<StackProps>;
    startContent?: Partial<BoxProps>;
    label?: Partial<TypographyProps>;
    value?: Partial<TypographyProps>;
    unit?: Partial<TypographyProps>;
    footer?: Partial<BoxProps>;
  };
};

const valuesBySize: Record<
  MetricsBoxSize,
  { valueProps: TypographyProps; unitProps?: TypographyProps }
> = {
  xs: { valueProps: { variant: 'A1' }, unitProps: { variant: 'L10R' } },
  s: { valueProps: { variant: 'h6' } },
  m: { valueProps: { variant: 'h5' } },
  l: { valueProps: { variant: 'h4' } },
};

export const MetricsBox = ({
  size = 'm',
  slots: { startContent, label, infoTooltip, value, unit, footer } = {},
  slotProps,
  testId,
}: MetricsBoxProps) => {
  const values = valuesBySize[size];

  return (
    <Stack
      direction="row"
      gap={8}
      data-testid={testId}
      {...slotProps?.root}
      className={clsx(metricsBoxClassNames.root, slotProps?.root?.className)}
      sx={mergeSx(slotProps?.root?.sx)}
    >
      {!!startContent && (
        <Box
          mt={2}
          alignSelf="stretch"
          {...slotProps?.startContent}
          className={clsx(
            metricsBoxClassNames.startContent,
            slotProps?.startContent?.className
          )}
          sx={mergeSx(slotProps?.startContent?.sx)}
        >
          {startContent}
        </Box>
      )}
      <Stack gap={4}>
        <Stack direction="row" alignItems="center" gap={2}>
          <Typography
            variant="L12M"
            data-testid={testId ? `${testId}-label` : undefined}
            {...slotProps?.label}
            className={clsx(
              metricsBoxClassNames.label,
              slotProps?.label?.className
            )}
            sx={mergeSx(slotProps?.label?.sx)}
          >
            {label}
          </Typography>
          {!!infoTooltip && (
            <InfoTooltip
              title={infoTooltip}
              className={metricsBoxClassNames.infoTooltip}
            />
          )}
        </Stack>
        <Stack direction="row" gap={4} alignItems="baseline">
          <Typography
            {...values.valueProps}
            data-testid={testId ? `${testId}-value` : undefined}
            {...slotProps?.value}
            className={clsx(
              metricsBoxClassNames.value,
              slotProps?.value?.className
            )}
            sx={mergeSx(slotProps?.value?.sx)}
          >
            {value}
          </Typography>
          {!!unit && (
            <Typography
              variant="L12R"
              color={tokens.theme.fg.structural.secondary}
              {...values.unitProps}
              data-testid={testId ? `${testId}-unit` : undefined}
              {...slotProps?.unit}
              className={clsx(
                metricsBoxClassNames.unit,
                slotProps?.unit?.className
              )}
              sx={mergeSx(slotProps?.unit?.sx)}
            >
              {unit}
            </Typography>
          )}
        </Stack>
        {footer && (
          <Box
            data-testid={testId ? `${testId}-footer` : undefined}
            {...slotProps?.footer}
            className={clsx(
              metricsBoxClassNames.footer,
              slotProps?.footer?.className
            )}
            sx={mergeSx(slotProps?.footer?.sx)}
          >
            {footer}
          </Box>
        )}
      </Stack>
    </Stack>
  );
};
